import { BookingTotal } from "./BookingTotal"
import { derivedDates } from "../dateSelection"
import { formatDate, money } from "../format"
import type { Addon, Product, Quote, RangeChoice } from "../types"

type SummaryInputs = {
    product: Product
    image: string
    choice: RangeChoice | null
    dates: ReturnType<typeof derivedDates>
    postcode: string
    quantity: number
    deliveryFeePence: number | undefined
    quote: Quote | null
    selectedAddons: Addon[]
}

export function getBookingSummary({ product, image, choice, dates, postcode, quantity, deliveryFeePence, quote, selectedAddons }: SummaryInputs) {
    if (!choice || !dates) return null

    const hire = quote?.lineItems.find((item) => item.key === "hire")?.amountPence ?? choice.pricePence * quantity
    const delivery = quote?.lineItems.find((item) => item.key === "delivery")?.amountPence ?? deliveryFeePence
    const addons = quote
        ? quote.lineItems.filter((item) => item.key.startsWith("accessory"))
        : selectedAddons.map((addon) => ({ key: `accessory:${addon.id}`, label: addon.name, amountPence: addon.pricePence }))
    const total = quote?.totalPence ?? hire + (delivery ?? 0) + addons.reduce((sum, item) => sum + item.amountPence, 0)

    return {
        productName: product.name, image, quantity, postcode, hireLabel: choice.label,
        addonCount: addons.length,
        details: [
            { label: "Delivery date", value: formatDate(dates.deliveryDate) },
            { label: "Collection date", value: formatDate(dates.collectionDate) },
            { label: "Hire duration", value: choice.label },
            ...(postcode ? [{ label: "Delivery postcode", value: postcode }] : []),
        ],
        costs: [
            ...(hire !== total ? [{ key: "hire", label: "Hire charge", amountPence: hire }] : []),
            ...addons,
            ...(delivery !== undefined ? [{ key: "delivery", label: "Delivery & collection from you", amountPence: delivery }] : []),
        ],
        total,
    }
}

export type BookingSummaryData = NonNullable<ReturnType<typeof getBookingSummary>>

export function BookingSummaryContent({ summary, layout, onEdit, expanded = true, totalLabel }: { summary: BookingSummaryData; layout: "sidebar" | "disclosure"; onEdit?: () => void; expanded?: boolean; totalLabel?: string }) {
    const disclosure = layout === "disclosure"
    const prefix = disclosure ? "ah-disclosure" : "ah-summary"
    const total = <BookingTotal className={disclosure ? "ah-disclosure-total" : "ah-total"} totalPence={summary.total} label={totalLabel} />
    return <>
        <dl className={`${prefix}-details`}>
            {summary.details.map(({ label, value }) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </dl>
        {(summary.costs.length > 0 || disclosure) && <div className={`${prefix}-costs`} aria-label="Cost breakdown">
            {disclosure && <strong className="ah-disclosure-cost-title">Cost breakdown</strong>}
            {summary.costs.map((item) => <div key={item.key} className={item.key === "delivery" ? "ah-delivery-cost-row" : item.key.startsWith("accessory") ? "ah-addon-cost-row" : undefined}>
                <span>{item.key === "delivery" && <><span aria-hidden="true">🚚</span>{" "}</>}{item.label}</span><strong className={item.key === "delivery" && item.amountPence === 0 ? "ah-free-charge" : undefined}>{item.key === "delivery" && item.amountPence === 0 ? "FREE" : money(item.amountPence)}</strong>
            </div>)}
            {disclosure && total}
        </div>}
        {!disclosure && total}
        {onEdit && <button type="button" className="ah-edit-booking-button" tabIndex={expanded ? 0 : -1} onClick={onEdit}>Edit booking</button>}
    </>
}
